import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_V1, authFetch, formatAmount, formatAmountCompact } from '../constants';
import { useEntities } from '../hooks/useEntities';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import TabBar from '../components/ui/TabBar';
import FilterBar from '../components/ui/FilterBar';
import ActionButton from '../components/ui/ActionButton';
import StatusBadge from '../components/ui/StatusBadge';
import Modal from '../components/ui/Modal';

// ── Source-provenance badge (AI-native: every row knows where it came from)
const SOURCE_LABELS: Record<string, { label: string; bg: string; fg: string; icon: string }> = {
  CONTRACT_INTELLIGENCE: { label: 'Contract AI', bg: '#dbeafe', fg: '#1e40af', icon: '📄' },
  CAP_TABLE_CSV:         { label: 'Cap Table',    bg: '#dcfce7', fg: '#166534', icon: '📊' },
  BOARD_RESOLUTION:      { label: 'Board Min',    bg: '#ede9fe', fg: '#6d28d9', icon: '📜' },
  VALUATION_409A:        { label: '409A',         bg: '#fef3c7', fg: '#92400e', icon: '💰' },
  STOCK_PLAN:            { label: 'Stock Plan',   bg: '#fce7f3', fg: '#9d174d', icon: '🎁' },
  SAFE_NOTE:             { label: 'SAFE',         bg: '#ecfccb', fg: '#3f6212', icon: '🔒' },
  CONVERTIBLE_NOTE:      { label: 'Conv Note',    bg: '#ffedd5', fg: '#9a3412', icon: '📝' },
  SAFE_CONVERSION:       { label: 'SAFE → Eq',    bg: '#d1fae5', fg: '#065f46', icon: '🔓' },
  NOTE_CONVERSION:       { label: 'Note → Eq',    bg: '#fed7aa', fg: '#7c2d12', icon: '🔄' },
  CONVERSATIONAL:        { label: 'Chat',         bg: '#e0f2fe', fg: '#075985', icon: '💬' },
  MANUAL:                { label: 'Manual',       bg: '#f3f4f6', fg: '#4b5563', icon: '✏️' },
  D160_BACKFILL:         { label: 'Backfill',     bg: '#f3f4f6', fg: '#6b7280', icon: '🔄' },
  LEGACY_UNKNOWN:        { label: 'Not tagged',   bg: '#f9fafb', fg: '#9ca3af', icon: '—' },
};

function SourceBadge({ method, documentName, confidence }: { method?: string | null; documentName?: string | null; confidence?: number | null }) {
  if (!method) {
    return (
      <span title="No source tagged" style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 7px',
        borderRadius: 4, fontSize: 10, fontWeight: 600,
        background: '#f9fafb', color: '#9ca3af',
      }}>—</span>
    );
  }
  const s = SOURCE_LABELS[method] || { label: method, bg: '#f3f4f6', fg: '#4b5563', icon: '?' };
  const tip = `${s.label}${documentName ? ` — ${documentName}` : ''}${confidence != null ? ` (${confidence}% confidence)` : ''}`;
  return (
    <span title={tip} style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 7px',
      borderRadius: 4, fontSize: 10, fontWeight: 600,
      background: s.bg, color: s.fg,
    }}>
      <span>{s.icon}</span>{s.label}
      {confidence != null && confidence > 0 && (
        <span style={{ opacity: 0.7, fontSize: 9 }}>{confidence}%</span>
      )}
    </span>
  );
}

const API = `${API_V1}/equity`;
const PAGE_SIZE = 20;
const TABS = ['Cap Table', 'Transactions', 'Grants', 'Dividends', 'Equity Rollforward'];
const TXN_TYPES = ['ISSUANCE', 'BUYBACK', 'CONVERSION', 'STOCK_SPLIT', 'SBC_VEST', 'SBC_EXPENSE', 'TRANSFER', 'REISSUE'];

const emptyClassForm = {
  class_name: '', class_code: '', entity_code: '', shares_authorized: '',
  par_value: '0.01', currency: 'USD', is_preferred: false, dividend_rate: '',
  is_cumulative: false, is_convertible: false, conversion_ratio: '',
  voting_rights_per_share: '1', liquidation_preference: '',
};

export default function EquityManagement() {
  const navigate = useNavigate();
  const entities = useEntities();
  const { entity } = useGlobalEntity();
  const [tab, setTab] = useState('Cap Table');
  const [summary, setSummary] = useState<any>(null);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  // Cap-table CSV import (M22-AI Phase 1.3)
  const [showImport, setShowImport] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importEntity, setImportEntity] = useState('');
  const [importPreview, setImportPreview] = useState<any>(null);
  const [importing, setImporting] = useState(false);

  // Cap Table
  const [classes, setClasses] = useState<any[]>([]);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [expandedData, setExpandedData] = useState<any>(null);
  const [showCreateClass, setShowCreateClass] = useState(false);
  const [classForm, setClassForm] = useState({ ...emptyClassForm });
  const [glPreview, setGlPreview] = useState<any>(null);

  // Transaction modals
  const [showIssue, setShowIssue] = useState(false);
  const [showBuyback, setShowBuyback] = useState(false);
  const [showSBC, setShowSBC] = useState(false);
  const [txnClassId, setTxnClassId] = useState<number | null>(null);
  const [issueForm, setIssueForm] = useState({ shares: '', price_per_share: '', counterparty: '', transaction_date: '' });
  const [buybackForm, setBuybackForm] = useState({ shares: '', price_per_share: '', counterparty: '', transaction_date: '' });
  const [sbcForm, setSbcForm] = useState({ shares_vested: '', fair_value_per_unit: '', counterparty: '', transaction_date: '' });

  // Transactions tab
  const [transactions, setTransactions] = useState<any[]>([]);
  const [txnTotal, setTxnTotal] = useState(0);
  const [txnPage, setTxnPage] = useState(0);
  const [txnTypeFilter, setTxnTypeFilter] = useState('');

  // Dividends tab
  const [dividends, setDividends] = useState<any[]>([]);
  const [divStatusFilter, setDivStatusFilter] = useState('');
  const [showDeclareDiv, setShowDeclareDiv] = useState(false);
  const [divClassId, setDivClassId] = useState<number | null>(null);
  const [divForm, setDivForm] = useState({ dividend_per_share: '', declaration_date: '', record_date: '', payment_date: '' });

  // Rollforward
  const [rollforward, setRollforward] = useState<any>(null);

  // Grants (Phase 2.3)
  const [grants, setGrants] = useState<any[]>([]);
  const [grantsLoading, setGrantsLoading] = useState(false);
  const [expandedGrantId, setExpandedGrantId] = useState<number | null>(null);
  const [grantDetail, setGrantDetail] = useState<any>(null);

  // Conversational editor (Phase 2.4)
  const [showChat, setShowChat] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [chatPreview, setChatPreview] = useState<any>(null);
  const [chatBusy, setChatBusy] = useState(false);
  const [chatExamples] = useState([
    'issue 100K Series A Preferred to ABC Capital at $1.20 per share',
    'buy back 5,000 Common from John Smith at $5.00',
    'declare a $0.10/share quarterly dividend on Series A Preferred',
    'vest 1,000 RSUs for Jane Doe at $2.85 FMV',
  ]);

  const showToast = (msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 4000);
  };

  const loadSummary = useCallback(async () => {
    try {
      const res = await authFetch(withEntityParam(`${API}/summary`, entity));
      if (res.ok) setSummary(await res.json());
    } catch (e) { console.error(e); }
  }, [entity]);

  const loadClasses = useCallback(async () => {
    try {
      const res = await authFetch(withEntityParam(`${API}/classes?limit=50`, entity));
      if (res.ok) {
        const d = await res.json();
        setClasses(d.items || []);
      }
    } catch (e) { console.error(e); }
  }, [entity]);

  const loadTransactions = useCallback(async () => {
    const p = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(txnPage * PAGE_SIZE) });
    if (txnTypeFilter) p.set('transaction_type', txnTypeFilter);
    try {
      const res = await authFetch(withEntityParam(`${API}/transactions?${p}`, entity));
      if (res.ok) {
        const d = await res.json();
        setTransactions(d.items || []);
        setTxnTotal(d.total || 0);
      }
    } catch (e) { console.error(e); }
  }, [txnPage, txnTypeFilter, entity]);

  const loadDividends = useCallback(async () => {
    const p = new URLSearchParams({ limit: '50' });
    if (divStatusFilter) p.set('status', divStatusFilter);
    try {
      const res = await authFetch(withEntityParam(`${API}/dividends?${p}`, entity));
      if (res.ok) {
        const d = await res.json();
        setDividends(d.items || []);
      }
    } catch (e) { console.error(e); }
  }, [divStatusFilter, entity]);

  const loadRollforward = useCallback(async () => {
    try {
      const res = await authFetch(withEntityParam(`${API}/rollforward`, entity));
      if (res.ok) setRollforward(await res.json());
    } catch (e) { console.error(e); }
  }, [entity]);

  const loadGrants = useCallback(async () => {
    setGrantsLoading(true);
    try {
      const res = await authFetch(withEntityParam(`${API}/grants?limit=100`, entity));
      if (res.ok) {
        const d = await res.json();
        setGrants(d.items || []);
      }
    } catch (e) { console.error(e); }
    finally { setGrantsLoading(false); }
  }, [entity]);

  // Conversational (Phase 2.4) — preview-then-execute
  const handleChatInterpret = async () => {
    if (!chatInput || chatInput.trim().length < 5) {
      showToast('Type at least 5 chars of intent', false); return;
    }
    setChatBusy(true); setChatPreview(null);
    try {
      const body: any = { utterance: chatInput.trim() };
      if (entity) body.entity_code = entity;
      const r = await authFetch(`${API}/conversational/interpret`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (r.ok) {
        setChatPreview(await r.json());
      } else {
        const e = await r.json().catch(() => ({}));
        showToast(`Interpret failed: ${e.detail || `HTTP ${r.status}`}`, false);
      }
    } catch (e: any) {
      showToast(`Error: ${e?.message || 'Network'}`, false);
    } finally {
      setChatBusy(false);
    }
  };

  const handleChatExecute = async () => {
    if (!chatPreview || chatPreview.action === 'NONE') return;
    setChatBusy(true);
    try {
      const r = await authFetch(`${API}/conversational/execute${entity ? `?entity_code=${encodeURIComponent(entity)}` : ''}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: chatPreview.action,
          params: chatPreview.params,
          source_utterance: chatPreview.utterance || chatInput,
        }),
      });
      if (r.ok) {
        showToast(`Done: ${chatPreview.summary || chatPreview.action.toLowerCase()}`);
        setShowChat(false); setChatInput(''); setChatPreview(null);
        loadClasses(); loadSummary();
        if (tab === 'Transactions') loadTransactions();
        if (tab === 'Dividends') loadDividends();
      } else {
        const e = await r.json().catch(() => ({}));
        showToast(`Execute failed: ${e.detail || `HTTP ${r.status}`}`, false);
      }
    } catch (e: any) {
      showToast(`Error: ${e?.message || 'Network'}`, false);
    } finally {
      setChatBusy(false);
    }
  };

  const toggleGrantExpand = async (id: number) => {
    if (expandedGrantId === id) { setExpandedGrantId(null); setGrantDetail(null); return; }
    try {
      const res = await authFetch(`${API}/grants/${id}`);
      if (res.ok) { setGrantDetail(await res.json()); setExpandedGrantId(id); }
    } catch (e) { console.error(e); }
  };

  useEffect(() => { loadSummary(); loadClasses(); }, [loadSummary, loadClasses]);
  useEffect(() => { if (tab === 'Transactions') loadTransactions(); }, [tab, loadTransactions]);
  useEffect(() => { if (tab === 'Dividends') loadDividends(); }, [tab, loadDividends]);
  useEffect(() => { if (tab === 'Equity Rollforward') loadRollforward(); }, [tab, loadRollforward]);
  useEffect(() => { if (tab === 'Grants') loadGrants(); }, [tab, loadGrants]);

  // Load GL preview when create modal opens or entity changes
  useEffect(() => {
    if (!showCreateClass) return;
    const p = new URLSearchParams();
    if (classForm.entity_code) p.set('entity_code', classForm.entity_code);
    authFetch(`${API}/preview-gl?${p}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => setGlPreview(d))
      .catch(() => setGlPreview(null));
  }, [showCreateClass, classForm.entity_code]);

  const toggleExpand = async (id: number) => {
    if (expandedId === id) { setExpandedId(null); setExpandedData(null); return; }
    try {
      const res = await authFetch(`${API}/classes/${id}`);
      if (res.ok) { setExpandedData(await res.json()); setExpandedId(id); }
    } catch (e) { console.error(e); }
  };

  const handleCreateClass = async () => {
    // Pre-flight validation
    if (!classForm.class_name) { showToast('Class Name is required', false); return; }
    if (!classForm.shares_authorized || parseInt(classForm.shares_authorized) <= 0) {
      showToast('Shares Authorized is required and must be > 0', false); return;
    }

    try {
      const body: any = { ...classForm };
      body.shares_authorized = parseInt(body.shares_authorized);
      body.par_value = parseFloat(body.par_value) || 0;
      body.voting_rights_per_share = parseFloat(body.voting_rights_per_share) || 1;
      if (body.dividend_rate) body.dividend_rate = parseFloat(body.dividend_rate); else delete body.dividend_rate;
      if (body.conversion_ratio) body.conversion_ratio = parseFloat(body.conversion_ratio); else delete body.conversion_ratio;
      if (body.liquidation_preference) body.liquidation_preference = parseFloat(body.liquidation_preference); else delete body.liquidation_preference;
      if (!body.entity_code) delete body.entity_code;
      if (!body.class_code) delete body.class_code;

      const res = await authFetch(`${API}/classes`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (res.ok) {
        const d = await res.json();
        showToast(`Share class "${d.class_name}" created in DRAFT. GL: ${d.gl_resolution_method || 'N/A'} (${d.gl_resolution_confidence || 0}%)`);
        setShowCreateClass(false); setClassForm({ ...emptyClassForm }); loadClasses(); loadSummary();
      } else {
        const e = await res.json().catch(() => ({}));
        showToast(`Create failed: ${e.detail || `HTTP ${res.status}`}`, false);
      }
    } catch (e: any) {
      showToast(`Error: ${e?.message || 'Network error'}`, false);
    }
  };

  const handleSubmitClass = async (id: number) => {
    try {
      const r = await authFetch(`${API}/classes/${id}/submit-for-approval`, { method: 'POST' });
      if (r.ok) { showToast('Submitted for approval'); loadClasses(); loadSummary(); setExpandedId(null); }
      else { const e = await r.json().catch(() => ({})); showToast(e.detail || 'Failed', false); }
    } catch (e) { showToast('Error', false); }
  };

  const handleApproveClass = async (id: number) => {
    try {
      const r = await authFetch(`${API}/classes/${id}/approve`, { method: 'POST' });
      if (r.ok) { showToast('Share class approved & active'); loadClasses(); loadSummary(); setExpandedId(null); }
      else { const e = await r.json().catch(() => ({})); showToast(e.detail || 'Failed', false); }
    } catch (e) { showToast('Error', false); }
  };

  const handleIssue = async () => {
    if (!txnClassId) return;
    try {
      const body: any = { shares: parseInt(issueForm.shares), price_per_share: parseFloat(issueForm.price_per_share) };
      if (issueForm.counterparty) body.counterparty = issueForm.counterparty;
      if (issueForm.transaction_date) body.transaction_date = issueForm.transaction_date;
      const res = await authFetch(`${API}/classes/${txnClassId}/issue`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (res.ok) {
        const d = await res.json();
        showToast(`Issued ${issueForm.shares} shares. Par: ${formatAmount(d.par_total)}, APIC: ${formatAmount(d.apic_amount)}`);
        setShowIssue(false); loadClasses(); loadSummary(); if (tab === 'Transactions') loadTransactions();
      } else { const e = await res.json().catch(() => ({})); showToast(e.detail || 'Issue failed', false); }
    } catch (e) { showToast('Error issuing shares', false); }
  };

  const handleBuyback = async () => {
    if (!txnClassId) return;
    try {
      const body: any = { shares: parseInt(buybackForm.shares), price_per_share: parseFloat(buybackForm.price_per_share) };
      if (buybackForm.counterparty) body.counterparty = buybackForm.counterparty;
      if (buybackForm.transaction_date) body.transaction_date = buybackForm.transaction_date;
      const res = await authFetch(`${API}/classes/${txnClassId}/buyback`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (res.ok) { showToast(`Buyback recorded`); setShowBuyback(false); loadClasses(); loadSummary(); if (tab === 'Transactions') loadTransactions(); }
      else { const e = await res.json().catch(() => ({})); showToast(e.detail || 'Buyback failed', false); }
    } catch (e) { showToast('Error', false); }
  };

  const handleSBC = async () => {
    if (!txnClassId) return;
    try {
      const body: any = { shares_vested: parseInt(sbcForm.shares_vested), fair_value_per_unit: parseFloat(sbcForm.fair_value_per_unit) };
      if (sbcForm.counterparty) body.counterparty = sbcForm.counterparty;
      if (sbcForm.transaction_date) body.transaction_date = sbcForm.transaction_date;
      const res = await authFetch(`${API}/classes/${txnClassId}/sbc`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (res.ok) { const d = await res.json(); showToast(`SBC expense ${formatAmount(d.amount)} recorded`); setShowSBC(false); loadClasses(); loadSummary(); }
      else { const e = await res.json().catch(() => ({})); showToast(e.detail || 'SBC failed', false); }
    } catch (e) { showToast('Error', false); }
  };

  const handleDeclareDiv = async () => {
    if (!divClassId) return;
    try {
      const body: any = { dividend_per_share: parseFloat(divForm.dividend_per_share), declaration_date: divForm.declaration_date };
      if (divForm.record_date) body.record_date = divForm.record_date;
      if (divForm.payment_date) body.payment_date = divForm.payment_date;
      const res = await authFetch(`${API}/classes/${divClassId}/dividend`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (res.ok) { const d = await res.json(); showToast(`Dividend declared: ${formatAmount(d.total_amount)} total`); setShowDeclareDiv(false); loadDividends(); loadSummary(); }
      else { const e = await res.json().catch(() => ({})); showToast(e.detail || 'Failed', false); }
    } catch (e) { showToast('Error', false); }
  };

  const handleDivAction = async (divId: number, action: string) => {
    try {
      const res = await authFetch(`${API}/dividends/${divId}/${action}`, { method: 'POST' });
      if (res.ok) { showToast(`Dividend ${action}d`); loadDividends(); loadSummary(); }
      else { const e = await res.json().catch(() => ({})); showToast(e.detail || `${action} failed`, false); }
    } catch (e) { showToast('Error', false); }
  };

  const lbl = { fontSize: 10, fontWeight: 700, color: 'var(--text-hint)', textTransform: 'uppercase' as const, marginBottom: 2 };
  const cellPad = { padding: '6px 10px' };
  const thStyle = { ...cellPad, fontSize: 10, fontWeight: 700, color: 'var(--text-hint)', textTransform: 'uppercase' as const, textAlign: 'left' as const, borderBottom: '2px solid var(--border-subtle)' };
  const tdStyle = { ...cellPad, fontSize: 13, borderBottom: '1px solid var(--border-subtle)' };
  const inputStyle = { width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border-subtle)', fontSize: 13 };

  const openTxnModal = (classId: number, type: 'issue' | 'buyback' | 'sbc') => {
    setTxnClassId(classId);
    if (type === 'issue') { setIssueForm({ shares: '', price_per_share: '', counterparty: '', transaction_date: '' }); setShowIssue(true); }
    if (type === 'buyback') { setBuybackForm({ shares: '', price_per_share: '', counterparty: '', transaction_date: '' }); setShowBuyback(true); }
    if (type === 'sbc') { setSbcForm({ shares_vested: '', fair_value_per_unit: '', counterparty: '', transaction_date: '' }); setShowSBC(true); }
  };

  const openDivModal = (classId: number) => {
    setDivClassId(classId);
    setDivForm({ dividend_per_share: '', declaration_date: '', record_date: '', payment_date: '' });
    setShowDeclareDiv(true);
  };

  // Cap-table CSV import flow: 2 steps — preview, then commit.
  const handlePreviewImport = async () => {
    if (!importFile) { showToast('Select a CSV file first', false); return; }
    setImporting(true);
    setImportPreview(null);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      const url = `${API}/import/cap-table?commit=false${importEntity ? `&entity_code=${encodeURIComponent(importEntity)}` : ''}`;
      const r = await authFetch(url, { method: 'POST', body: fd });
      if (r.ok) {
        const d = await r.json();
        setImportPreview(d);
      } else {
        const e = await r.json().catch(() => ({}));
        showToast(`Preview failed: ${e.detail || `HTTP ${r.status}`}`, false);
      }
    } catch (e: any) {
      showToast(`Error: ${e?.message || 'Network error'}`, false);
    } finally {
      setImporting(false);
    }
  };

  const handleCommitImport = async () => {
    if (!importFile) { showToast('Select a CSV file first', false); return; }
    if (!importEntity) { showToast('Pick an entity before committing', false); return; }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      const url = `${API}/import/cap-table?commit=true&entity_code=${encodeURIComponent(importEntity)}`;
      const r = await authFetch(url, { method: 'POST', body: fd });
      if (r.ok) {
        const d = await r.json();
        const parts = [
          `${d.classes_created} new`,
          `${d.classes_updated} updated`,
        ];
        if (d.classes_adopted) parts.push(`${d.classes_adopted} adopted`);
        const txnParts = [`${d.transactions_created} new issuances`];
        if (d.transactions_skipped_duplicate) {
          txnParts.push(`${d.transactions_skipped_duplicate} skipped (duplicates)`);
        }
        showToast(`Imported: ${parts.join(' + ')} classes · ${txnParts.join(', ')}`);
        setShowImport(false); setImportFile(null); setImportPreview(null); setImportEntity('');
        loadClasses(); loadSummary(); if (tab === 'Transactions') loadTransactions();
      } else {
        const e = await r.json().catch(() => ({}));
        showToast(`Import failed: ${e.detail || `HTTP ${r.status}`}`, false);
      }
    } catch (e: any) {
      showToast(`Error: ${e?.message || 'Network error'}`, false);
    } finally {
      setImporting(false);
    }
  };

  return (
    <PageLayout title="Equity Management" subtitle="Cap table, share transactions, dividends, and equity rollforward — AI extracts from your docs first; manual entry is the fallback."
      actions={[
        { label: '📄 Upload Doc (Subscription / Charter / Board)', onClick: () => navigate('/contracts/intelligence?source=equity') },
        { label: '💬 Ask AI', variant: 'secondary', onClick: () => { setShowChat(true); setChatInput(''); setChatPreview(null); } },
        { label: '📊 Import Cap Table CSV', variant: 'secondary', onClick: () => { setShowImport(true); setImportFile(null); setImportPreview(null); setImportEntity(entity || ''); } },
        { label: '+ Add manually', variant: 'secondary', onClick: () => setShowCreateClass(true) },
      ]}>

      {toast && (
        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 9999, padding: '10px 20px',
          borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600, maxWidth: 500,
          background: toast.ok ? '#16a34a' : '#ef4444' }}>{toast.msg}</div>
      )}

      {summary && (
        <KpiGrid items={[
          { label: 'Total Equity', value: formatAmountCompact(summary.total_equity) },
          { label: 'Shares Outstanding', value: summary.total_outstanding?.toLocaleString() || '0' },
          { label: 'Basic EPS', value: `$${summary.basic_eps?.toFixed(4) || '0'}` },
          { label: 'Book Value / Share', value: formatAmountCompact(summary.book_value_per_share) },
          { label: 'Treasury Cost', value: formatAmountCompact(summary.treasury_cost), color: summary.treasury_cost > 0 ? 'var(--negative)' : undefined },
          { label: 'Dividend Yield', value: `${summary.dividend_yield_pct || 0}%` },
        ]} />
      )}

      <div style={{ marginTop: 16 }}>
        <TabBar tabs={TABS.map(t => ({ key: t, label: t }))} active={tab} onChange={v => setTab(v)} />
      </div>

      {/* ═══ TAB: Cap Table ═══ */}
      {tab === 'Cap Table' && classes.length === 0 && (
        <div style={{ margin: '40px auto', maxWidth: 720, padding: '32px 28px', borderRadius: 12,
          background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', textAlign: 'center' }}>
          <div style={{ fontSize: 28, marginBottom: 6 }}>📊</div>
          <h3 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 6px', color: 'var(--navy-900)' }}>
            No share classes yet
          </h3>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 22px', lineHeight: 1.5 }}>
            AgentCFO is AI-native. Three signals create your cap table automatically —
            no 12-field wizard required:
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'stretch', maxWidth: 480, margin: '0 auto' }}>
            <button type="button" onClick={() => navigate('/contracts/intelligence?source=equity')}
              style={{ padding: '12px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                background: '#1e3a5f', color: '#fff', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
              <div>📄 Upload a subscription agreement, charter, or board resolution</div>
              <div style={{ fontSize: 11, opacity: 0.8, fontWeight: 400, marginTop: 4 }}>
                Contract Intelligence extracts class, shares, price, par, dividend rate, voting → auto-creates share class + issuance JE
              </div>
            </button>
            <button type="button" onClick={() => { setShowImport(true); setImportFile(null); setImportPreview(null); setImportEntity(entity || ''); }}
              style={{ padding: '12px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                background: '#fff', color: 'var(--navy-900)', border: '1px solid var(--border-default)', cursor: 'pointer', textAlign: 'left' }}>
              <div>📊 Drop your Carta / Pulley / AngelList / Shoobx cap table CSV</div>
              <div style={{ fontSize: 11, opacity: 0.7, fontWeight: 400, marginTop: 4 }}>
                Format auto-detected. Per-class preview before commit. Historical issuances skip JE posting.
              </div>
            </button>
            <button type="button" onClick={() => setShowCreateClass(true)}
              style={{ padding: '10px 16px', borderRadius: 8, fontSize: 12, fontWeight: 500,
                background: 'transparent', color: 'var(--text-secondary)', border: 'none', cursor: 'pointer' }}>
              or add a single class manually →
            </button>
          </div>
        </div>
      )}
      {tab === 'Cap Table' && classes.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 8 }}>
          <thead>
            <tr>
              {['Class', 'Type', 'Authorized', 'Issued', 'Outstanding', 'Treasury', 'Par Value', 'Status', 'Source', 'Actions'].map(h => (
                <th key={h} style={thStyle}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {classes.map(sc => (
              <>
                <tr key={sc.id} onClick={() => toggleExpand(sc.id)}
                  style={{ cursor: 'pointer', background: expandedId === sc.id ? 'var(--accent-subtle)' : 'transparent' }}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{sc.class_name}</td>
                  <td style={tdStyle}>{sc.is_preferred ? 'Preferred' : 'Common'}</td>
                  <td style={tdStyle}>{sc.shares_authorized?.toLocaleString()}</td>
                  <td style={tdStyle}>{sc.shares_issued?.toLocaleString()}</td>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{sc.shares_outstanding?.toLocaleString()}</td>
                  <td style={{ ...tdStyle, color: sc.shares_in_treasury > 0 ? 'var(--negative)' : undefined }}>{sc.shares_in_treasury?.toLocaleString()}</td>
                  <td style={tdStyle}>${sc.par_value}</td>
                  <td style={tdStyle}><StatusBadge status={sc.lifecycle_status} /></td>
                  <td style={tdStyle}>
                    <SourceBadge method={sc.source_method} documentName={sc.source_document_name} confidence={sc.source_confidence} />
                  </td>
                  <td style={tdStyle} onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {sc.lifecycle_status === 'DRAFT' && (
                        <ActionButton label="Submit" size="sm" onClick={() => handleSubmitClass(sc.id)} />
                      )}
                      {sc.lifecycle_status === 'PENDING_APPROVAL' && (
                        <ActionButton label="Approve" size="sm" variant="success" onClick={() => handleApproveClass(sc.id)} />
                      )}
                      {sc.lifecycle_status === 'ACTIVE' && (
                        <>
                          <ActionButton label="Issue" size="sm" onClick={() => openTxnModal(sc.id, 'issue')} />
                          <ActionButton label="Buyback" size="sm" variant="secondary" onClick={() => openTxnModal(sc.id, 'buyback')} />
                          <ActionButton label="SBC" size="sm" variant="secondary" onClick={() => openTxnModal(sc.id, 'sbc')} />
                          <ActionButton label="Dividend" size="sm" variant="success" onClick={() => openDivModal(sc.id)} />
                        </>
                      )}
                    </div>
                  </td>
                </tr>

                {expandedId === sc.id && expandedData && (
                  <tr key={`${sc.id}-detail`}>
                    <td colSpan={10} style={{ padding: '0 14px 16px 14px', background: 'var(--accent-subtle)' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
                        {/* Detail grid */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 8 }}>
                          {[
                            { l: 'Par Value', v: `$${expandedData.par_value}` },
                            { l: 'Currency', v: expandedData.currency },
                            { l: 'Voting Rights', v: `${expandedData.voting_rights_per_share}/share` },
                            { l: 'Dividend Rate', v: expandedData.dividend_rate ? `${(expandedData.dividend_rate * 100).toFixed(2)}%` : 'N/A' },
                            { l: 'Convertible', v: expandedData.is_convertible ? `Yes (${expandedData.conversion_ratio}:1)` : 'No' },
                            { l: 'Liquidation Pref', v: expandedData.liquidation_preference ? formatAmount(expandedData.liquidation_preference) : 'None' },
                          ].map(d => (
                            <div key={d.l} style={{ padding: 8, background: 'var(--bg-card)', borderRadius: 6 }}>
                              <div style={lbl}>{d.l}</div>
                              <div style={{ fontSize: 12 }}>{d.v}</div>
                            </div>
                          ))}
                        </div>

                        {/* 409A Fair Market Value (Phase 2.2) */}
                        <div style={{ padding: 10, borderRadius: 8, background: 'var(--bg-card)',
                          borderLeft: expandedData.fair_value_per_share ? '3px solid #f59e0b' : '3px solid var(--border-subtle)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                            <div style={{ fontSize: 12, fontWeight: 700 }}>
                              💰 409A Fair Market Value
                            </div>
                            {expandedData.fair_value_per_share ? (
                              <div style={{ fontSize: 11, color: 'var(--text-hint)' }}>
                                {expandedData.fair_value_method || 'Method n/a'} · sourced from {expandedData.fair_value_source_document_name || 'unknown'}
                              </div>
                            ) : (
                              <div style={{ fontSize: 11, color: 'var(--text-hint)' }}>
                                No 409A on file — upload a valuation report to populate
                              </div>
                            )}
                          </div>
                          {expandedData.fair_value_per_share && (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 6 }}>
                              <div>
                                <div style={lbl}>FMV/Share</div>
                                <div style={{ fontSize: 14, fontWeight: 700, color: '#92400e' }}>
                                  ${Number(expandedData.fair_value_per_share).toFixed(4)}
                                </div>
                              </div>
                              <div>
                                <div style={lbl}>As of</div>
                                <div style={{ fontSize: 12 }}>{expandedData.fair_value_as_of_date || '-'}</div>
                              </div>
                              <div>
                                <div style={lbl}>Implied Equity Value</div>
                                <div style={{ fontSize: 12 }}>
                                  {expandedData.shares_outstanding && expandedData.fair_value_per_share
                                    ? formatAmount(Number(expandedData.shares_outstanding) * Number(expandedData.fair_value_per_share))
                                    : '-'}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* GL Accounts */}
                        <div style={{ padding: 12, borderRadius: 8, background: 'var(--bg-card)' }}>
                          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>GL Accounts (resolved from CoA)</div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                            {[
                              { l: 'Share Capital', v: expandedData.share_capital_account },
                              { l: 'APIC', v: expandedData.apic_account },
                              { l: 'Treasury Stock', v: expandedData.treasury_stock_account },
                              { l: 'Dividends Payable', v: expandedData.dividends_payable_account },
                              { l: 'Retained Earnings', v: expandedData.retained_earnings_account },
                              { l: 'Cash / Bank', v: expandedData.cash_account },
                              { l: 'SBC Expense', v: expandedData.sbc_expense_account },
                            ].map(d => (
                              <div key={d.l}>
                                <div style={lbl}>{d.l}</div>
                                <div style={{ fontSize: 12, fontFamily: 'monospace', color: d.v ? 'var(--accent)' : 'var(--text-hint)' }}>{d.v || 'Not resolved'}</div>
                              </div>
                            ))}
                          </div>
                          <div style={{ fontSize: 10, color: 'var(--text-hint)', marginTop: 4 }}>
                            Method: {expandedData.gl_resolution_method || 'N/A'} | Confidence: {expandedData.gl_resolution_confidence ?? 'N/A'}%
                          </div>
                        </div>

                        {/* Recent Transactions */}
                        {expandedData.transactions && expandedData.transactions.length > 0 && (
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 4 }}>RECENT TRANSACTIONS ({expandedData.transactions.length})</div>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                              <thead><tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                                {['Date', 'Type', 'Shares', 'Price', 'Amount', 'Counterparty', 'JE'].map(h => (
                                  <th key={h} style={{ textAlign: 'left', padding: '4px 8px', fontSize: 10, fontWeight: 700 }}>{h}</th>
                                ))}
                              </tr></thead>
                              <tbody>
                                {expandedData.transactions.map((t: any) => (
                                  <tr key={t.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                                    <td style={{ padding: '4px 8px' }}>{t.transaction_date}</td>
                                    <td style={{ padding: '4px 8px' }}><StatusBadge status={t.transaction_type} size="sm" /></td>
                                    <td style={{ padding: '4px 8px' }}>{t.shares?.toLocaleString()}</td>
                                    <td style={{ padding: '4px 8px' }}>{t.price_per_share ? `$${t.price_per_share}` : '-'}</td>
                                    <td style={{ padding: '4px 8px', fontWeight: 600 }}>{formatAmount(t.total_amount)}</td>
                                    <td style={{ padding: '4px 8px' }}>{t.counterparty || '-'}</td>
                                    <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>{t.je_id || '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      )}

      {/* ═══ TAB: Transactions ═══ */}
      {tab === 'Transactions' && (
        <>
          <FilterBar filters={[
            { key: 'type', label: 'Type', type: 'select', value: txnTypeFilter,
              onChange: (v: string) => { setTxnTypeFilter(v); setTxnPage(0); },
              options: TXN_TYPES.map(t => ({ value: t, label: t.replace(/_/g, ' ') })) },
          ]} />
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr>
              {['Date', 'Type', 'Share Class', 'Shares', 'Price/Share', 'Amount', 'Counterparty', 'JE', 'Source'].map(h => (
                <th key={h} style={thStyle}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {transactions.length === 0 ? (
                <tr><td colSpan={9} style={{ ...tdStyle, textAlign: 'center', color: 'var(--text-hint)' }}>No transactions yet.</td></tr>
              ) : transactions.map(t => (
                <tr key={t.id}>
                  <td style={tdStyle}>{t.transaction_date}</td>
                  <td style={tdStyle}><StatusBadge status={t.transaction_type} size="sm" /></td>
                  <td style={tdStyle}>{t.class_name || '-'}</td>
                  <td style={tdStyle}>{t.shares?.toLocaleString()}</td>
                  <td style={tdStyle}>{t.price_per_share ? `$${t.price_per_share}` : '-'}</td>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{formatAmount(t.total_amount)}</td>
                  <td style={tdStyle}>{t.counterparty || '-'}</td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{t.je_id || '-'}</td>
                  <td style={tdStyle}>
                    <SourceBadge method={t.source_method} documentName={t.source_document_name} confidence={t.source_confidence} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {txnTotal > PAGE_SIZE && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 12, alignItems: 'center' }}>
              <ActionButton label="PREV" size="sm" variant="secondary" disabled={txnPage === 0} onClick={() => setTxnPage(Math.max(0, txnPage - 1))} />
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{txnPage * PAGE_SIZE + 1}-{Math.min((txnPage + 1) * PAGE_SIZE, txnTotal)} of {txnTotal}</span>
              <ActionButton label="NEXT" size="sm" variant="secondary" disabled={(txnPage + 1) * PAGE_SIZE >= txnTotal} onClick={() => setTxnPage(txnPage + 1)} />
            </div>
          )}
        </>
      )}

      {/* ═══ TAB: Grants (M22-AI Phase 2.3c) ═══ */}
      {tab === 'Grants' && (
        <>
          {grants.length === 0 && !grantsLoading ? (
            <div style={{ margin: '40px auto', maxWidth: 720, padding: '32px 28px', borderRadius: 12,
              background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', textAlign: 'center' }}>
              <div style={{ fontSize: 28, marginBottom: 6 }}>🎁</div>
              <h3 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 6px', color: 'var(--navy-900)' }}>
                No grants yet
              </h3>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 18px', lineHeight: 1.5 }}>
                Upload an ISO / NSO / RSU grant agreement via Contract Intelligence.
                The cascade auto-creates the grant, vesting schedule, and scheduled vest events.
                When each event matures, the scheduler posts SBC expense using your 409A FMV — no manual entry.
              </p>
              <button type="button" onClick={() => navigate('/contracts/intelligence?source=equity')}
                style={{ padding: '10px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                  background: '#1e3a5f', color: '#fff', border: 'none', cursor: 'pointer' }}>
                📄 Upload Grant Agreement
              </button>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr>
                {['Grantee', 'Class', 'Type', 'Total', 'Vested', 'Unvested', 'Strike', 'Vesting', 'Status', 'Source'].map(h => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {grants.map(g => (
                  <>
                    <tr key={g.id} onClick={() => toggleGrantExpand(g.id)}
                      style={{ cursor: 'pointer', background: expandedGrantId === g.id ? 'var(--accent-subtle)' : 'transparent' }}>
                      <td style={{ ...tdStyle, fontWeight: 600 }}>
                        {g.grantee_name}
                        {g.grantee_email && <span style={{ display: 'block', fontSize: 10, color: 'var(--text-hint)' }}>{g.grantee_email}</span>}
                      </td>
                      <td style={tdStyle}>{g.share_class_name || `#${g.share_class_id}`}</td>
                      <td style={tdStyle}><StatusBadge status={g.grant_type} size="sm" /></td>
                      <td style={tdStyle}>{g.total_shares?.toLocaleString()}</td>
                      <td style={{ ...tdStyle, color: g.shares_vested > 0 ? 'var(--positive)' : undefined }}>
                        {g.shares_vested?.toLocaleString()}
                      </td>
                      <td style={tdStyle}>{g.shares_unvested?.toLocaleString()}</td>
                      <td style={tdStyle}>{g.exercise_price ? `$${Number(g.exercise_price).toFixed(4)}` : '—'}</td>
                      <td style={{ ...tdStyle, fontSize: 11 }}>
                        {g.vesting_type
                          ? `${g.vesting_type}, ${g.total_months}mo, ${g.cliff_months}mo cliff`
                          : '—'}
                      </td>
                      <td style={tdStyle}><StatusBadge status={g.lifecycle_status} /></td>
                      <td style={tdStyle}>
                        <SourceBadge method={g.source_method} documentName={g.source_document_name} confidence={g.source_confidence} />
                      </td>
                    </tr>
                    {expandedGrantId === g.id && grantDetail && (
                      <tr key={`${g.id}-detail`}>
                        <td colSpan={10} style={{ padding: '0 14px 16px 14px', background: 'var(--accent-subtle)' }}>
                          <div style={{ marginTop: 8 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>
                              VESTING WATERFALL — {grantDetail.vesting_events?.length || 0} events
                            </div>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                              <thead><tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                                {['Vest Date', 'Shares', 'Status', 'FMV/Share', 'SBC Expense', 'JE', 'Posted At'].map(h => (
                                  <th key={h} style={{ textAlign: 'left', padding: '4px 8px', fontSize: 10, fontWeight: 700, color: 'var(--text-hint)' }}>{h}</th>
                                ))}
                              </tr></thead>
                              <tbody>
                                {grantDetail.vesting_events?.map((ev: any) => (
                                  <tr key={ev.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                                    <td style={{ padding: '4px 8px' }}>{ev.vest_date}</td>
                                    <td style={{ padding: '4px 8px', fontWeight: 600 }}>{ev.shares_vested?.toLocaleString()}</td>
                                    <td style={{ padding: '4px 8px' }}><StatusBadge status={ev.status} size="sm" /></td>
                                    <td style={{ padding: '4px 8px' }}>{ev.fair_value_per_share_at_vest ? `$${Number(ev.fair_value_per_share_at_vest).toFixed(4)}` : '—'}</td>
                                    <td style={{ padding: '4px 8px', fontWeight: 600 }}>{ev.sbc_expense_amount ? formatAmount(ev.sbc_expense_amount) : '—'}</td>
                                    <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>{ev.je_id || '—'}</td>
                                    <td style={{ padding: '4px 8px', fontSize: 10, color: 'var(--text-hint)' }}>
                                      {ev.posted_at ? ev.posted_at.replace('T', ' ').slice(0, 16) : (ev.skip_reason || '')}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {grantDetail.vesting_events?.length === 0 && (
                              <div style={{ fontSize: 11, color: 'var(--text-hint)', fontStyle: 'italic', padding: 8 }}>
                                No vesting events generated for this grant.
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {/* ═══ TAB: Dividends ═══ */}
      {tab === 'Dividends' && (
        <>
          <FilterBar filters={[
            { key: 'status', label: 'Status', type: 'select', value: divStatusFilter,
              onChange: (v: string) => setDivStatusFilter(v),
              options: ['DECLARED', 'APPROVED', 'PAID'].map(s => ({ value: s, label: s })) },
          ]} />
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr>
              {['Declaration', 'Share Class', '$/Share', 'Total', 'Record Date', 'Payment Date', 'Status', 'Actions'].map(h => (
                <th key={h} style={thStyle}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {dividends.length === 0 ? (
                <tr><td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: 'var(--text-hint)' }}>No dividends declared yet.</td></tr>
              ) : dividends.map(d => (
                <tr key={d.id}>
                  <td style={tdStyle}>{d.declaration_date}</td>
                  <td style={tdStyle}>{d.class_name || '-'}</td>
                  <td style={tdStyle}>${d.dividend_per_share}</td>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{formatAmount(d.total_amount)}</td>
                  <td style={tdStyle}>{d.record_date || '-'}</td>
                  <td style={tdStyle}>{d.payment_date || '-'}</td>
                  <td style={tdStyle}><StatusBadge status={d.lifecycle_status} /></td>
                  <td style={tdStyle} onClick={e => e.stopPropagation()}>
                    {d.lifecycle_status === 'DECLARED' && <ActionButton label="Approve" size="sm" onClick={() => handleDivAction(d.id, 'approve')} />}
                    {d.lifecycle_status === 'APPROVED' && <ActionButton label="Pay" size="sm" variant="success" onClick={() => handleDivAction(d.id, 'pay')} />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {/* ═══ TAB: Equity Rollforward ═══ */}
      {tab === 'Equity Rollforward' && rollforward && (
        <div style={{ marginTop: 12 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr>
              {['Component', 'Opening', 'Issuances', 'Buybacks', 'SBC', 'Dividends', 'Net Income', 'Other', 'Closing'].map(h => (
                <th key={h} style={thStyle}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {Object.entries(rollforward.components).map(([key, comp]: [string, any]) => (
                <tr key={key}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</td>
                  <td style={tdStyle}>{formatAmount(comp.opening)}</td>
                  <td style={{ ...tdStyle, color: comp.issuances > 0 ? 'var(--positive)' : undefined }}>{comp.issuances ? formatAmount(comp.issuances) : '-'}</td>
                  <td style={{ ...tdStyle, color: comp.buybacks > 0 ? 'var(--negative)' : undefined }}>{comp.buybacks ? formatAmount(comp.buybacks) : '-'}</td>
                  <td style={tdStyle}>{comp.sbc ? formatAmount(comp.sbc) : '-'}</td>
                  <td style={{ ...tdStyle, color: comp.dividends < 0 ? 'var(--negative)' : undefined }}>{comp.dividends ? formatAmount(comp.dividends) : '-'}</td>
                  <td style={{ ...tdStyle, color: (comp.net_income || 0) >= 0 ? 'var(--positive)' : 'var(--negative)' }}>{comp.net_income != null ? formatAmount(comp.net_income) : '-'}</td>
                  <td style={tdStyle}>{comp.other ? formatAmount(comp.other) : '-'}</td>
                  <td style={{ ...tdStyle, fontWeight: 700 }}>{formatAmount(comp.closing)}</td>
                </tr>
              ))}
              {/* Total row */}
              <tr style={{ background: 'var(--accent-subtle)' }}>
                <td style={{ ...tdStyle, fontWeight: 700 }}>TOTAL</td>
                {['opening', 'issuances', 'buybacks', 'sbc', 'dividends', 'net_income', 'other', 'closing'].map(k => (
                  <td key={k} style={{ ...tdStyle, fontWeight: 700 }}>{formatAmount(rollforward.total[k] || 0)}</td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* ═══ Create Share Class Modal ═══ */}
      <Modal open={showCreateClass} onClose={() => setShowCreateClass(false)} title="New Share Class" size="lg"
        footer={<><ActionButton label="Cancel" variant="secondary" onClick={() => setShowCreateClass(false)} /><ActionButton label="Create" onClick={handleCreateClass} /></>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <div><div style={lbl}>Class Name *</div><input value={classForm.class_name} onChange={e => setClassForm({ ...classForm, class_name: e.target.value })} placeholder="Common" style={inputStyle} /></div>
            <div><div style={lbl}>Class Code</div><input value={classForm.class_code} onChange={e => setClassForm({ ...classForm, class_code: e.target.value })} placeholder="COM" style={inputStyle} /></div>
            <div>
              <div style={lbl}>Entity *</div>
              <select aria-label="Entity" value={classForm.entity_code} onChange={e => setClassForm({ ...classForm, entity_code: e.target.value })} style={inputStyle}>
                <option value="">Select entity...</option>
                {entities.map(en => (
                  <option key={en.entity_code} value={en.entity_code}>
                    {en.entity_code} — {en.entity_name} ({en.functional_currency})
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <div><div style={lbl}>Shares Authorized *</div><input type="number" value={classForm.shares_authorized} onChange={e => setClassForm({ ...classForm, shares_authorized: e.target.value })} style={inputStyle} /></div>
            <div><div style={lbl}>Par Value</div><input type="number" step="0.01" value={classForm.par_value} onChange={e => setClassForm({ ...classForm, par_value: e.target.value })} style={inputStyle} /></div>
            <div><div style={lbl}>Currency</div><input value={classForm.currency} onChange={e => setClassForm({ ...classForm, currency: e.target.value })} style={inputStyle} /></div>
          </div>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <label style={{ fontSize: 13, display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="checkbox" checked={classForm.is_preferred} onChange={e => setClassForm({ ...classForm, is_preferred: e.target.checked })} /> Preferred
            </label>
            <label style={{ fontSize: 13, display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="checkbox" checked={classForm.is_convertible} onChange={e => setClassForm({ ...classForm, is_convertible: e.target.checked })} /> Convertible
            </label>
            <label style={{ fontSize: 13, display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="checkbox" checked={classForm.is_cumulative} onChange={e => setClassForm({ ...classForm, is_cumulative: e.target.checked })} /> Cumulative
            </label>
          </div>
          {classForm.is_preferred && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div><div style={lbl}>Dividend Rate (decimal)</div><input type="number" step="0.001" value={classForm.dividend_rate} onChange={e => setClassForm({ ...classForm, dividend_rate: e.target.value })} placeholder="0.06" style={inputStyle} /></div>
              <div><div style={lbl}>Liquidation Preference</div><input type="number" value={classForm.liquidation_preference} onChange={e => setClassForm({ ...classForm, liquidation_preference: e.target.value })} style={inputStyle} /></div>
            </div>
          )}
          {classForm.is_convertible && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div><div style={lbl}>Conversion Ratio</div><input type="number" step="0.01" value={classForm.conversion_ratio} onChange={e => setClassForm({ ...classForm, conversion_ratio: e.target.value })} placeholder="1.5" style={inputStyle} /></div>
              <div><div style={lbl}>Voting Rights/Share</div><input type="number" step="0.1" value={classForm.voting_rights_per_share} onChange={e => setClassForm({ ...classForm, voting_rights_per_share: e.target.value })} style={inputStyle} /></div>
            </div>
          )}
          {/* GL Resolution Preview */}
          {glPreview && (
            <div style={{ padding: 12, borderRadius: 8, background: 'var(--accent-subtle)', border: '1px solid var(--border-subtle)', marginTop: 4 }}>
              <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 6, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>
                GL Accounts (auto-resolved from your CoA — {glPreview.gl_resolution_method || 'N/A'}, {glPreview.gl_resolution_confidence || 0}% confidence)
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                {[
                  { l: 'Share Capital', code: glPreview.share_capital_account },
                  { l: 'APIC', code: glPreview.apic_account },
                  { l: 'Treasury Stock', code: glPreview.treasury_stock_account },
                  { l: 'Dividends Payable', code: glPreview.dividends_payable_account },
                  { l: 'Retained Earnings', code: glPreview.retained_earnings_account },
                  { l: 'Cash / Bank', code: glPreview.cash_account },
                  { l: 'SBC Expense', code: glPreview.sbc_expense_account },
                ].map(d => (
                  <div key={d.l} style={{ fontSize: 11 }}>
                    <span style={{ color: 'var(--text-hint)' }}>{d.l}: </span>
                    {d.code ? (
                      <>
                        <span style={{ fontFamily: 'monospace', color: 'var(--accent)' }}>{d.code}</span>
                        {glPreview.account_names?.[d.code] && (
                          <span style={{ color: 'var(--text-secondary)' }}> — {glPreview.account_names[d.code]}</span>
                        )}
                      </>
                    ) : (
                      <span style={{ color: 'var(--negative)' }}>NOT RESOLVED — upload CoA or add manually</span>
                    )}
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-hint)', marginTop: 6, fontStyle: 'italic' }}>
                Note: subscription agreement upload via Contract Intelligence is the recommended entry point.
                You can edit GL accounts on the share class detail after creation.
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* ═══ Issue Shares Modal ═══ */}
      <Modal open={showIssue} onClose={() => setShowIssue(false)} title="Issue Shares" size="md"
        footer={<><ActionButton label="Cancel" variant="secondary" onClick={() => setShowIssue(false)} /><ActionButton label="Issue" onClick={handleIssue} /></>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div><div style={lbl}>Shares *</div><input type="number" value={issueForm.shares} onChange={e => setIssueForm({ ...issueForm, shares: e.target.value })} style={inputStyle} /></div>
            <div><div style={lbl}>Price/Share *</div><input type="number" step="0.01" value={issueForm.price_per_share} onChange={e => setIssueForm({ ...issueForm, price_per_share: e.target.value })} style={inputStyle} /></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div><div style={lbl}>Investor / Counterparty</div><input value={issueForm.counterparty} onChange={e => setIssueForm({ ...issueForm, counterparty: e.target.value })} style={inputStyle} /></div>
            <div><div style={lbl}>Date</div><input type="date" value={issueForm.transaction_date} onChange={e => setIssueForm({ ...issueForm, transaction_date: e.target.value })} style={inputStyle} /></div>
          </div>
          {issueForm.shares && issueForm.price_per_share && (
            <div style={{ padding: 10, background: '#f0fdf4', borderRadius: 6, fontSize: 12 }}>
              Total: <strong>{formatAmount(parseInt(issueForm.shares) * parseFloat(issueForm.price_per_share))}</strong>
            </div>
          )}
        </div>
      </Modal>

      {/* ═══ Buyback Modal ═══ */}
      <Modal open={showBuyback} onClose={() => setShowBuyback(false)} title="Share Buyback" size="md"
        footer={<><ActionButton label="Cancel" variant="secondary" onClick={() => setShowBuyback(false)} /><ActionButton label="Record Buyback" variant="danger" onClick={handleBuyback} /></>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div><div style={lbl}>Shares *</div><input type="number" value={buybackForm.shares} onChange={e => setBuybackForm({ ...buybackForm, shares: e.target.value })} style={inputStyle} /></div>
            <div><div style={lbl}>Price/Share *</div><input type="number" step="0.01" value={buybackForm.price_per_share} onChange={e => setBuybackForm({ ...buybackForm, price_per_share: e.target.value })} style={inputStyle} /></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div><div style={lbl}>Counterparty</div><input value={buybackForm.counterparty} onChange={e => setBuybackForm({ ...buybackForm, counterparty: e.target.value })} style={inputStyle} /></div>
            <div><div style={lbl}>Date</div><input type="date" value={buybackForm.transaction_date} onChange={e => setBuybackForm({ ...buybackForm, transaction_date: e.target.value })} style={inputStyle} /></div>
          </div>
        </div>
      </Modal>

      {/* ═══ SBC Modal ═══ */}
      <Modal open={showSBC} onClose={() => setShowSBC(false)} title="Stock-Based Compensation Expense" size="md"
        footer={<><ActionButton label="Cancel" variant="secondary" onClick={() => setShowSBC(false)} /><ActionButton label="Record SBC" onClick={handleSBC} /></>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div><div style={lbl}>Units Vested *</div><input type="number" value={sbcForm.shares_vested} onChange={e => setSbcForm({ ...sbcForm, shares_vested: e.target.value })} style={inputStyle} /></div>
            <div><div style={lbl}>Fair Value/Unit *</div><input type="number" step="0.01" value={sbcForm.fair_value_per_unit} onChange={e => setSbcForm({ ...sbcForm, fair_value_per_unit: e.target.value })} style={inputStyle} /></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div><div style={lbl}>Employee / Grantee</div><input value={sbcForm.counterparty} onChange={e => setSbcForm({ ...sbcForm, counterparty: e.target.value })} style={inputStyle} /></div>
            <div><div style={lbl}>Date</div><input type="date" value={sbcForm.transaction_date} onChange={e => setSbcForm({ ...sbcForm, transaction_date: e.target.value })} style={inputStyle} /></div>
          </div>
        </div>
      </Modal>

      {/* ═══ Declare Dividend Modal ═══ */}
      <Modal open={showDeclareDiv} onClose={() => setShowDeclareDiv(false)} title="Declare Dividend" size="md"
        footer={<><ActionButton label="Cancel" variant="secondary" onClick={() => setShowDeclareDiv(false)} /><ActionButton label="Declare" variant="success" onClick={handleDeclareDiv} /></>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div><div style={lbl}>Dividend Per Share *</div><input type="number" step="0.01" value={divForm.dividend_per_share} onChange={e => setDivForm({ ...divForm, dividend_per_share: e.target.value })} style={inputStyle} /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <div><div style={lbl}>Declaration Date *</div><input type="date" value={divForm.declaration_date} onChange={e => setDivForm({ ...divForm, declaration_date: e.target.value })} style={inputStyle} /></div>
            <div><div style={lbl}>Record Date</div><input type="date" value={divForm.record_date} onChange={e => setDivForm({ ...divForm, record_date: e.target.value })} style={inputStyle} /></div>
            <div><div style={lbl}>Payment Date</div><input type="date" value={divForm.payment_date} onChange={e => setDivForm({ ...divForm, payment_date: e.target.value })} style={inputStyle} /></div>
          </div>
        </div>
      </Modal>

      {/* ═══ Conversational Editor Modal (M22-AI Phase 2.4) ═══ */}
      <Modal open={showChat} onClose={() => { setShowChat(false); setChatInput(''); setChatPreview(null); }}
        title="Ask AI — Equity Action" size="lg"
        footer={
          <>
            <ActionButton label="Close" variant="secondary" onClick={() => { setShowChat(false); setChatInput(''); setChatPreview(null); }} />
            {!chatPreview ? (
              <ActionButton label={chatBusy ? 'Parsing...' : 'Interpret'} onClick={handleChatInterpret} disabled={!chatInput.trim() || chatBusy} />
            ) : (
              <ActionButton
                label={chatBusy ? 'Executing...' : (chatPreview.action === 'NONE' ? "Can't execute" : `Execute ${chatPreview.action}`)}
                variant="success"
                onClick={handleChatExecute}
                disabled={chatBusy || chatPreview.action === 'NONE'}
              />
            )}
          </>
        }>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            Type an equity action in plain English. The platform parses it,
            shows you the structured action, and posts it after your confirm —
            stamped <code>source_method = CONVERSATIONAL</code> with your
            original utterance visible on hover of the badge.
          </div>

          <div>
            <div style={lbl}>Your Request</div>
            <textarea
              value={chatInput}
              onChange={e => { setChatInput(e.target.value); setChatPreview(null); }}
              placeholder="e.g. issue 100K Series A Preferred to ABC Capital at $1.20 per share"
              rows={3}
              aria-label="Equity action utterance"
              style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
            />
          </div>

          {!chatPreview && (
            <div>
              <div style={lbl}>Try one of these</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {chatExamples.map((ex, i) => (
                  <button key={i} type="button" onClick={() => setChatInput(ex)}
                    style={{ textAlign: 'left', padding: '6px 10px', borderRadius: 6,
                      fontSize: 12, background: 'transparent', color: 'var(--text-secondary)',
                      border: '1px solid var(--border-subtle)', cursor: 'pointer' }}>
                    "{ex}"
                  </button>
                ))}
              </div>
            </div>
          )}

          {chatPreview && (
            <div style={{ padding: 14, borderRadius: 8, background: 'var(--accent-subtle)',
              border: '1px solid var(--border-subtle)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 700 }}>
                  Proposed action:{' '}
                  <span style={{ color: chatPreview.action === 'NONE' ? 'var(--negative)' : 'var(--accent)' }}>
                    {chatPreview.action}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                  Confidence: {chatPreview.confidence}% · via {chatPreview.llm_provider || 'llm'}/{chatPreview.llm_model || ''}
                </div>
              </div>

              <div style={{ fontSize: 13, marginBottom: 10, padding: 8, background: 'var(--bg-card)', borderRadius: 6 }}>
                {chatPreview.summary || '(no summary)'}
              </div>

              {chatPreview.params && Object.keys(chatPreview.params).length > 0 && (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, marginBottom: 8 }}>
                  <tbody>
                    {Object.entries(chatPreview.params).filter(([_, v]) => v !== null && v !== undefined && v !== '').map(([k, v]) => (
                      <tr key={k} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                        <td style={{ padding: '4px 8px', fontWeight: 600, width: '40%', color: 'var(--text-hint)' }}>{k}</td>
                        <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>{String(v)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {chatPreview.warnings && chatPreview.warnings.length > 0 && (
                <div style={{ marginTop: 8, padding: 8, background: '#fffbeb', borderRadius: 6, fontSize: 11, color: '#92400e' }}>
                  <strong>Warnings:</strong>
                  <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                    {chatPreview.warnings.map((w: string, i: number) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}

              {chatPreview.action !== 'NONE' && (
                <div style={{ marginTop: 10, fontSize: 11, color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                  On Execute: dispatches to the equity service with{' '}
                  <code>source_method = CONVERSATIONAL</code>. The badge on the
                  resulting row will show your original utterance on hover.
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* ═══ Import Cap Table CSV Modal (M22-AI Phase 1.3) ═══ */}
      <Modal open={showImport} onClose={() => { setShowImport(false); setImportFile(null); setImportPreview(null); }} title="Import Cap Table CSV" size="lg"
        footer={
          <>
            <ActionButton label="Cancel" variant="secondary" onClick={() => { setShowImport(false); setImportFile(null); setImportPreview(null); }} />
            {!importPreview ? (
              <ActionButton label={importing ? 'Parsing…' : 'Preview'} onClick={handlePreviewImport} disabled={!importFile || importing} />
            ) : (
              <ActionButton label={importing ? 'Importing…' : `Commit Import (${importPreview.parsed_rows} rows)`} variant="success" onClick={handleCommitImport} disabled={importing || !importEntity} />
            )}
          </>
        }>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            Drop your <strong>Carta</strong>, <strong>Pulley</strong>, <strong>AngelList</strong>, <strong>Shoobx</strong>, or generic
            cap-table CSV. Format auto-detected. Historical issuances are recorded
            as audit records (no JE posting — your GL already has them).
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
            <div>
              <div style={lbl}>CSV File *</div>
              <input type="file" accept=".csv,text/csv"
                aria-label="Cap table CSV file"
                title="Select a cap table CSV file (Carta / Pulley / AngelList / Shoobx / generic)"
                onChange={e => { setImportFile(e.target.files?.[0] || null); setImportPreview(null); }}
                style={inputStyle} />
              {importFile && <div style={{ fontSize: 11, color: 'var(--text-hint)', marginTop: 4 }}>{importFile.name} ({(importFile.size / 1024).toFixed(1)} KB)</div>}
            </div>
            <div>
              <div style={lbl}>Entity *</div>
              <select aria-label="Entity" value={importEntity} onChange={e => setImportEntity(e.target.value)} style={inputStyle}>
                <option value="">Select entity...</option>
                {entities.map(en => (
                  <option key={en.entity_code} value={en.entity_code}>
                    {en.entity_code} — {en.entity_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {importPreview && (
            <div style={{ padding: 14, borderRadius: 8, background: 'var(--accent-subtle)', border: '1px solid var(--border-subtle)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 700 }}>
                  Preview — Format detected: <span style={{ color: 'var(--accent)' }}>{importPreview.format_detected}</span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                  {importPreview.parsed_rows} parsed · {importPreview.skipped_rows} skipped · {importPreview.total_holders} holders
                </div>
              </div>

              {importPreview.classes && importPreview.classes.length > 0 && (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 10 }}>
                  <thead><tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                    {['Class', 'Type', 'Total Shares', 'Holders', 'Avg Price', 'Par', 'Earliest', 'Latest'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '5px 8px', fontSize: 10, fontWeight: 700, color: 'var(--text-hint)' }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {importPreview.classes.map((c: any) => (
                      <tr key={c.class_name} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                        <td style={{ padding: '5px 8px', fontWeight: 600 }}>{c.class_name}</td>
                        <td style={{ padding: '5px 8px' }}>{c.class_type}</td>
                        <td style={{ padding: '5px 8px' }}>{c.shares_total?.toLocaleString()}</td>
                        <td style={{ padding: '5px 8px' }}>{c.holder_count}</td>
                        <td style={{ padding: '5px 8px' }}>{c.avg_price ? `$${c.avg_price.toFixed(4)}` : '-'}</td>
                        <td style={{ padding: '5px 8px' }}>{c.par_value != null ? `$${c.par_value}` : '-'}</td>
                        <td style={{ padding: '5px 8px' }}>{c.earliest_issue || '-'}</td>
                        <td style={{ padding: '5px 8px' }}>{c.latest_issue || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {importPreview.warnings && importPreview.warnings.length > 0 && (
                <details style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                  <summary style={{ cursor: 'pointer', color: 'var(--negative)' }}>
                    {importPreview.warnings.length} warning{importPreview.warnings.length === 1 ? '' : 's'}
                  </summary>
                  <ul style={{ margin: '6px 0 0 16px', padding: 0 }}>
                    {importPreview.warnings.slice(0, 10).map((w: string, i: number) => <li key={i}>{w}</li>)}
                  </ul>
                </details>
              )}

              <div style={{ marginTop: 10, padding: 8, background: '#fffbeb', borderRadius: 6, fontSize: 11, color: '#92400e' }}>
                <strong>What happens on commit:</strong> {importPreview.classes?.length || 0} share class{importPreview.classes?.length === 1 ? '' : 'es'} (find-or-create by name + entity), {importPreview.parsed_rows} historical ISSUANCE rows (no JE — already in GL), every row stamped <code>source_method = CAP_TABLE_CSV</code> + filename.
              </div>
            </div>
          )}
        </div>
      </Modal>
    </PageLayout>
  );
}
